/**
 * Icon Registry for backward compatibility.
 *
 * Announcements store icon names as Material Icon strings (e.g. "link", "laptop") in the database.
 * This registry maps those legacy names to Fluent UI icon components, allowing us to remove
 * the @mui/material dependency and the Google Material Icons font while keeping existing
 * data fully functional.
 */
import {
  Attach24Regular,
  Book24Filled,
  Bookmark24Filled,
  Briefcase24Filled,
  BuildingHome24Filled,
  CalendarLtr24Regular,
  Camera24Filled,
  Chat24Filled,
  ClipboardTaskListLtr24Filled,
  Clock24Regular,
  Cloud24Filled,
  Fire24Regular,
  Heart24Filled,
  Home24Filled,
  Image24Filled,
  Laptop24Regular,
  Lightbulb24Filled,
  Link24Regular,
  Mail24Regular,
  Mention24Regular,
  Mic24Filled,
  Money24Filled,
  Open24Regular,
  PeopleCommunity24Filled,
  Rocket24Filled,
  Search24Regular,
  Star24Filled,
  TreeEvergreen20Filled,
  VehicleBus24Filled,
} from '@fluentui/react-icons';
import React from 'react';

type FluentIcon = React.FC<React.SVGAttributes<SVGElement>>;

const iconMap: Record<string, FluentIcon> = {
  link: Link24Regular,
  laptop: Laptop24Regular,
  access_time: Clock24Regular,
  airport_shuttle: VehicleBus24Filled,
  alternate_email: Mention24Regular,
  assignment: ClipboardTaskListLtr24Filled,
  attach_file: Attach24Regular,
  book: Book24Filled,
  bookmark: Bookmark24Filled,
  calendar_month: CalendarLtr24Regular,
  business_center: Briefcase24Filled,
  chat: Chat24Filled,
  cloud: Cloud24Filled,
  euro: Money24Filled,
  favorite: Heart24Filled,
  forest: TreeEvergreen20Filled,
  grade: Star24Filled,
  groups: PeopleCommunity24Filled,
  house: Home24Filled,
  home_work: BuildingHome24Filled,
  image: Image24Filled,
  keyboard_voice: Mic24Filled,
  lightbulb: Lightbulb24Filled,
  local_fire_department: Fire24Regular,
  mail_outline: Mail24Regular,
  open_in_new: Open24Regular,
  photo_camera: Camera24Filled,
  rocket: Rocket24Filled,
  search: Search24Regular,
  star: Star24Filled,
};

export interface MaterialIconProps {
  name: string;
  className?: string;
  style?: React.CSSProperties;
}

export const MaterialIcon: React.FC<MaterialIconProps> = ({ name, className, style }) => {
  const Icon = iconMap[name];
  if (!Icon) return null;
  return <Icon className={className} style={style} />;
};

export const getFluentIcon = (name: string): FluentIcon | undefined => iconMap[name];

export default iconMap;
